/**
 * F05 — leaving the progress screen must not imply the server stopped.
 *
 * Flow has no server-side cancellation: processing_jobs only moves through
 * pending → running → done | failed, and nothing consumes a cancel request.
 * The audit's instruction for exactly that case is to stop pretending —
 * label the action "Stop waiting", say plainly that processing continues,
 * and keep a way back to the job.
 *
 * So this module does two things: it words the action honestly, and it keeps
 * a durable handle on the job so a reload still reaches it.
 */

export const JOB_HANDLE_KEY = 'flow-active-job';

// What the two buttons actually do — named so they can't be confused.
export const STOP_WAITING = 'stop_waiting';   // leave the screen; server keeps going
export const RETRY_STATUS = 'retry_status';   // re-read job status; creates nothing
export const START_NEW_JOB = 'start_new_job'; // a fresh, billable run

/** Only this one starts paid work. Used to keep Retry off that path. */
export function createsNewWork(action) {
  return action === START_NEW_JOB;
}

// ── durable handle ──────────────────────────────────────────────────────────

export function saveJobHandle(storage, handle) {
  if (!handle || (!handle.jobId && !handle.articleId)) return false;
  try {
    storage.setItem(JOB_HANDLE_KEY, JSON.stringify({ ...handle, at: Date.now() }));
    return true;
  } catch { return false; }
}

export function readJobHandle(storage) {
  try {
    const raw = storage.getItem(JOB_HANDLE_KEY);
    if (!raw) return null;
    const h = JSON.parse(raw);
    if (!h || (!h.jobId && !h.articleId)) return null;
    return h;
  } catch { return null; }
}

export function clearJobHandle(storage) {
  try { storage.removeItem(JOB_HANDLE_KEY); } catch { /* nothing to do */ }
}

/** A handle is worth offering while it could still resolve to something. */
export function isResumable(handle, { maxAgeMs = 24 * 60 * 60 * 1000, now = Date.now() } = {}) {
  if (!handle) return false;
  if (!handle.jobId && !handle.articleId) return false;
  if (!handle.at) return true;
  return (now - handle.at) < maxAgeMs;
}

// ── wording ─────────────────────────────────────────────────────────────────

/**
 * What the stop-waiting control should say. It never claims the work was
 * cancelled, because it wasn't.
 */
export function stopWaitingCopy({ jobId, articleId } = {}) {
  const canReopen = Boolean(jobId || articleId);
  return {
    label: 'Stop waiting',
    title: 'Processing continues in the background',
    body: canReopen
      ? 'This only stops the wait on this screen. Your recording keeps processing '
        + 'and you can come back to it.'
      : 'This only stops the wait on this screen. Your recording keeps processing '
        + 'on the server.',
    canReopen,
    inputsKept: true,
  };
}

/** The banner offering a job back after a reload. */
export function resumeCopy(handle) {
  if (!handle) return null;
  return {
    title: 'You have a recording still processing',
    body: handle.title
      ? `"${handle.title}" was still being processed when you left.`
      : 'A recording was still being processed when you left.',
    openLabel: handle.articleId ? 'Open draft' : 'Check progress',
    dismissLabel: 'Dismiss',
  };
}
